import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Table, Button, Badge, Spinner, Alert, Form,
} from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2 } from '../../utils/icons';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';

type TipoResposta = 'TEXTO_LIVRE' | 'MULTIPLA_ESCOLHA' | 'SIM_NAO' | 'PERCENTUAL';

interface PQuestion {
  id: number;
  pergunta: string;
  dominio_nome?: string;
  dominio_indice?: string;
  framework_id?: number;
  framework_nome?: string;
  tipo_resposta: TipoResposta;
  is_modelo: boolean;
  peso?: number;
  checklist_id?: number | null;
}

const dominioDisplay = (q: PQuestion): string => {
  if (!q.dominio_nome) return '';
  return q.dominio_indice ? `${q.dominio_indice} – ${q.dominio_nome}` : q.dominio_nome;
};

interface Framework { id: number; nome: string; }

const TIPO_LABELS: Record<string, string> = {
  TEXTO_LIVRE: 'Texto',
  MULTIPLA_ESCOLHA: 'Múltipla',
  SIM_NAO: 'Sim/Não',
  PERCENTUAL: '%',
};

const TIPO_COLORS: Record<string, string> = {
  SIM_NAO: 'success',
  PERCENTUAL: 'primary',
  MULTIPLA_ESCOLHA: 'warning',
  TEXTO_LIVRE: 'secondary',
};

const QuestionsListPage: React.FC = () => {
  const [questions, setQuestions] = useState<PQuestion[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [qRes, fwRes] = await Promise.all([api.getQuestions(), api.getFrameworks()]);
      if (qRes.success && qRes.data) setQuestions(qRes.data as PQuestion[]);
      else setError('Falha ao buscar perguntas.');
      if (fwRes.success && fwRes.data) setFrameworks(fwRes.data as Framework[]);
      setLoading(false);
    };
    load();
  }, []);

  const handleDelete = async (q: PQuestion) => {
    if (!q.id) return;
    if (!window.confirm(`Excluir a pergunta?\n\n"${q.pergunta}"`)) return;
    const res = await api.deleteQuestion(q.id);
    if (res.success) setQuestions(prev => prev.filter(p => p.id !== q.id));
    else notify.error('Falha ao excluir.');
  };

  const filtered = frameworkFilter
    ? questions.filter(q => String(q.framework_id) === frameworkFilter)
    : questions;

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 align-items-center g-2">
        <Col>
          <h2 className="mb-0 fs-4">Perguntas</h2>
        </Col>
        <Col xs="auto">
          <Button variant="primary" size="sm" onClick={() => navigate('/app/perguntas')}>
            <FiPlus /> Nova Pergunta
          </Button>
        </Col>
      </Row>

      {/* Filtro framework */}
      <Row className="mb-3 g-2">
        <Col xs={12} sm={6} md={4}>
          <Form.Select
            size="sm"
            value={frameworkFilter}
            onChange={e => setFrameworkFilter(e.target.value)}
          >
            <option value="">Todos os Frameworks</option>
            {frameworks.map(fw => (
              <option key={fw.id} value={String(fw.id)}>{fw.nome}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <span className="text-muted small">{filtered.length} pergunta{filtered.length !== 1 ? 's' : ''}</span>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <Table responsive hover bordered className="mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: '30%' }}>Pergunta</th>
              <th className="d-none d-md-table-cell" style={{ width: '32%' }}>Domínio</th>
              <th style={{ width: '10%' }}>Tipo</th>
              <th className="d-none d-sm-table-cell" style={{ width: '16%' }}>Framework</th>
              <th style={{ width: '12%' }} className="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-muted py-4">Nenhuma pergunta encontrada.</td></tr>
            ) : filtered.map(q => (
              <tr key={q.id}>
                <td>
                  <div>{q.pergunta}</div>
                  {/* Info extra em mobile */}
                  <div className="d-md-none text-muted small mt-1">{dominioDisplay(q)}</div>
                  <div className="d-flex gap-1 mt-1">
                    {q.checklist_id && <Badge bg="info" pill style={{ fontSize: '0.65rem' }}>CL</Badge>}
                    {q.peso !== undefined && q.peso !== 100 && (
                      <Badge bg="light" text="dark" pill style={{ fontSize: '0.65rem' }}>P:{q.peso}</Badge>
                    )}
                  </div>
                </td>
                <td className="d-none d-md-table-cell">
                  <span className="text-muted small">{dominioDisplay(q) || '—'}</span>
                </td>
                <td>
                  <Badge bg={TIPO_COLORS[q.tipo_resposta] || 'secondary'} style={{ fontSize: '0.75rem' }}>
                    {TIPO_LABELS[q.tipo_resposta] || q.tipo_resposta}
                  </Badge>
                </td>
                <td className="d-none d-sm-table-cell">
                  {q.framework_nome
                    ? <span className="small">{q.framework_nome}</span>
                    : <span className="text-muted">—</span>}
                </td>
                <td className="text-center">
                  <div className="d-flex gap-1 justify-content-center">
                    <Button size="sm" variant="outline-primary" onClick={() => navigate(`/app/perguntas/editar/${q.id}`)}>
                      <FiEdit2 />
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(q)}>
                      <FiTrash2 />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default QuestionsListPage;
